import { useId, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePropertyDocuments } from '@/hooks/useApi'
import { Download, FileText, Image as ImageIcon } from 'lucide-react'
import {
  PROPERTY_DOCUMENT_KINDS,
  type PropertyDocumentKind,
  type PropertyDocumentV1,
  type PropertyProfileDocumentSlot,
  type PropertyProfileSectionId,
} from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { apiErrorMessage } from '@/lib/api-error'
import { formatLocaleDateTime } from '@/lib/format-utils'
import { PROFILE_SECTIONS } from '@/lib/profile'
import { DOCUMENT_KIND_LABELS, formatBytes, uploadProblem } from '@/lib/profile-documents'

const CONTROL_CLASS =
  'h-10 w-full rounded-input border border-border bg-surface2 px-3 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50'

function DownloadLink({
  propertyId,
  document,
}: {
  propertyId: string
  document: PropertyDocumentV1
}) {
  return (
    <a
      href={api.propertyDocumentDownloadUrl(propertyId, document.id)}
      className="inline-flex items-center gap-1 text-sm text-mint hover:underline"
      aria-label={`Download ${document.filename}`}
      // A new tab: the file saves and the tab closes, and if the API's
      // sign-in has expired the sign-in page opens there, not over this page.
      target="_blank"
      rel="noreferrer"
    >
      <Download className="h-4 w-4" aria-hidden />
      Download
    </a>
  )
}

function SourceTag({ document }: { document: PropertyDocumentV1 }) {
  return (
    <Badge variant={document.source === 'ops' ? 'default' : 'secondary'} className="shrink-0">
      {document.source === 'ops' ? 'Ops' : 'Owner'}
    </Badge>
  )
}

interface SectionSlotsProps {
  propertyId: string
  slots: readonly PropertyProfileDocumentSlot[]
  sectionId: PropertyProfileSectionId
  documents: readonly PropertyDocumentV1[] | undefined
  /** The documents could not be loaded: said, rather than showing no slots. */
  failed?: boolean
  /** Whether each slot's `when` holds for this section's answers. */
  applies: (slot: PropertyProfileDocumentSlot) => boolean
}

/** A section's expected uploads: what is there, and which are still missing. */
export function SectionDocumentSlots({
  propertyId,
  slots,
  sectionId,
  documents,
  applies,
  failed,
}: SectionSlotsProps) {
  const shown = slots.filter(applies)
  if (shown.length === 0) return null
  if (failed) {
    return (
      <p className="mt-4 border-t border-border pt-3 text-sm text-danger">
        This section&apos;s documents could not be loaded.
      </p>
    )
  }
  if (documents === undefined) return null
  return (
    <div className="mt-4 space-y-2 border-t border-border pt-3">
      <h4 className="text-sm font-medium text-text">Documents</h4>
      <ul className="space-y-2">
        {shown.map(slot => {
          const matching = documents.filter(
            doc => doc.kind === slot.kind && doc.section_id === sectionId
          )
          return (
            <li key={slot.kind} className="text-sm">
              <div className="text-textMuted">{slot.label}</div>
              {matching.length === 0 ? (
                <div className="text-amber">Not uploaded</div>
              ) : (
                <ul className="space-y-1">
                  {matching.map(doc => (
                    <li key={doc.id} className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 wrap-break-word text-text">{doc.filename}</span>
                      <DownloadLink propertyId={propertyId} document={doc} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function UploadForm({ propertyId }: { propertyId: string }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const id = useId()
  const fileInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [kind, setKind] = useState<PropertyDocumentKind | ''>('')
  const [sectionId, setSectionId] = useState<PropertyProfileSectionId | ''>('')
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const upload = async () => {
    if (!file || !kind || uploading) return
    const problem = uploadProblem(file)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    setUploading(true)
    try {
      await api.uploadPropertyDocument(propertyId, file, kind, sectionId || null)
      void queryClient.invalidateQueries({ queryKey: ['property-documents', propertyId] })
      toast({ title: 'Uploaded', description: file.name })
      setFile(null)
      setKind('')
      setSectionId('')
      if (fileInput.current) fileInput.current.value = ''
    } catch (err) {
      // admin-api's copy is written for people; shown as it comes.
      setError(apiErrorMessage(err, 'The file could not be uploaded. Please try again.'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-3 rounded-card border border-border p-3">
      <div className="text-sm font-medium text-text">Upload for the owner</div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <label htmlFor={`${id}-kind`} className="text-xs text-textMuted">
            Kind
          </label>
          <select
            id={`${id}-kind`}
            className={CONTROL_CLASS}
            value={kind}
            onChange={e => setKind(e.target.value as PropertyDocumentKind | '')}
            disabled={uploading}
          >
            <option value="">Choose</option>
            {PROPERTY_DOCUMENT_KINDS.map(k => (
              <option key={k} value={k}>
                {DOCUMENT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-section`} className="text-xs text-textMuted">
            Section
          </label>
          <select
            id={`${id}-section`}
            className={CONTROL_CLASS}
            value={sectionId}
            onChange={e => setSectionId(e.target.value as PropertyProfileSectionId | '')}
            disabled={uploading}
          >
            <option value="">Photos and documents</option>
            {PROFILE_SECTIONS.map(section => (
              <option key={section.id} value={section.id}>
                {section.title}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-file`} className="text-xs text-textMuted">
            File (PDF or image, up to 25 MB)
          </label>
          <input
            id={`${id}-file`}
            ref={fileInput}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/heic,image/webp,.heic"
            className="block w-full text-sm text-textMuted file:mr-3 file:rounded-button file:border file:border-borderStrong file:bg-surface2 file:px-3 file:py-1.5 file:text-text"
            onChange={e => {
              setError(null)
              setFile(e.target.files?.[0] ?? null)
            }}
            disabled={uploading}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {error && (
          <p role="alert" className="mr-auto text-sm text-danger">
            {error}
          </p>
        )}
        <Button size="sm" onClick={() => void upload()} disabled={!file || !kind || uploading}>
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </div>
  )
}

function sectionTitle(sectionId: PropertyProfileSectionId | null) {
  return sectionId
    ? (PROFILE_SECTIONS.find(s => s.id === sectionId)?.title ?? sectionId)
    : 'Photos and documents'
}

/**
 * Every private document and photo of the property (CAP-1467): downloads go
 * through admin-api as attachments, so nothing is previewed inline and no
 * link works without the viewer's own Access session. Ops can add one here.
 */
export function ProfileDocumentsCard({
  propertyId,
  isActive,
}: {
  propertyId: string
  isActive: boolean
}) {
  const query = usePropertyDocuments(propertyId, isActive)
  const documents = query.data?.documents ?? []
  const photos = documents.filter(doc => doc.kind === 'photo')
  const files = documents.filter(doc => doc.kind !== 'photo')

  return (
    <Card data-testid="profile-documents">
      <CardHeader>
        <CardTitle>Documents and photos</CardTitle>
        <CardDescription>
          Private to Capehost and the owner. Files download; they are not shown in the browser.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {query.isLoading ? (
          <LoadingSkeleton className="h-24 w-full" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} compact />
        ) : documents.length === 0 ? (
          <p className="text-sm text-textMuted">No documents or photos yet.</p>
        ) : (
          <>
            {photos.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-text">Photos</h4>
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {photos.map(photo => (
                    <li
                      key={photo.id}
                      className="flex flex-col gap-2 rounded-card border border-border bg-surface2 p-3"
                    >
                      <ImageIcon className="h-8 w-8 text-textFaint" aria-hidden />
                      <span className="line-clamp-2 wrap-break-word text-xs text-text">
                        {photo.filename}
                      </span>
                      <span className="text-xs text-textMuted">
                        {formatBytes(photo.size_bytes)} · {sectionTitle(photo.section_id)}
                      </span>
                      <SourceTag document={photo} />
                      <DownloadLink propertyId={propertyId} document={photo} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {files.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-text">Documents</h4>
                <ul className="divide-y divide-border">
                  {files.map(doc => (
                    <li
                      key={doc.id}
                      className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm"
                    >
                      <div className="flex min-w-0 gap-2">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-textFaint" aria-hidden />
                        <div className="min-w-0">
                          <div className="wrap-break-word text-text">{doc.filename}</div>
                          <div className="text-xs text-textMuted">
                            {DOCUMENT_KIND_LABELS[doc.kind]} · {sectionTitle(doc.section_id)} ·{' '}
                            {formatBytes(doc.size_bytes)} ·{' '}
                            {formatLocaleDateTime(doc.uploaded_at, { fallback: '-' })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <SourceTag document={doc} />
                        <DownloadLink propertyId={propertyId} document={doc} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        <UploadForm key={propertyId} propertyId={propertyId} />
      </CardContent>
    </Card>
  )
}
